/**
 * Configuración → Empresa (`/configuracion/empresa`).
 *
 * Puerto de `Empresa.php` + `application/views/menu/empresa/index.php`. La
 * pantalla mantiene las dos ideas del panel anterior:
 *
 * 1. **Modo lectura por defecto**: los campos están bloqueados hasta pulsar
 *    *Editar* (allí se soltaban `readonly`/`disabled` uno por uno) y al guardar
 *    se pide confirmación, porque esto cambia los datos fiscales de la empresa.
 * 2. **Secciones**: los mismos bloques del panel anterior (plan, datos, API,
 *    opciones, fidelidad, integraciones y logos). La sección *Configuración de
 *    PDFs* y la pantalla *PDF* (plantilla de ticket) no se migraron: el usuario
 *    las retiró por obsoletas. Lo mismo con *Login y correo* (branding del login
 *    y credenciales de envío de correo), que la empresa tampoco usa: sus campos
 *    siguen guardados y se reenvían tal cual al guardar.
 *
 * Las series de comprobantes **no** se editan aquí (se administran en
 * Documentos Fiscales → Series), por eso la pantalla ya no las lista: sólo
 * quedan editables la serie de guía de remisión y la de cotización.
 */

import {
  Building2,
  CalendarClock,
  CreditCard,
  Crown,
  Image as IconoImagen,
  Landmark,
  Loader2,
  Pencil,
  Plug,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useAuthStore, selectUser } from '@/features/auth/auth.store';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency } from '@/lib/utils';

import { EmpresaLogos } from '../components/empresa-logos';
import { EmpresaPlanCard } from '../components/empresa-plan-card';
import { EmpresaPlanDialogo } from '../components/empresa-plan-dialogo';
import {
  useCambiarPlan,
  useConsultarRuc,
  useEmpresa,
  useGuardarEmpresa,
  useSubirImagenEmpresa,
} from '../empresa.api';
import {
  BANDERA,
  CIERRES,
  CREDITOS,
  FIDELIDADES,
  IDIOMAS,
  IMPRESION_CPE,
  MODOS_DESCUENTO,
  MODOS_TRABAJO,
  NFC,
  SI_NO,
  TIPOS_DOC_AUTOTICKETING,
  VISIBILIDAD,
  aBandera,
  aTextoBandera,
  construirPayload,
  extrasDe,
  textoModulos,
  type Opcion,
} from '../lib/empresa-form';
import { cargarUbigeo, distritosDe, provinciasDe, type OpcionUbigeo } from '@/lib/ubigeo';
import type {
  CampoImagenEmpresa,
  ConfigCompanyPayload,
  EmpresaConfig,
  EmpresaFormulario,
  EmpresaRespuesta,
} from '../empresa.types';

type Pestana =
  | 'plan'
  | 'datos'
  | 'api'
  | 'opciones'
  | 'app'
  | 'fidelidad'
  | 'integraciones'
  | 'logos';

const PESTANAS: readonly { id: Pestana; etiqueta: string; icon: typeof Crown }[] = [
  { id: 'plan', etiqueta: 'Plan contratado', icon: Crown },
  { id: 'datos', etiqueta: 'Datos de la empresa', icon: Building2 },
  { id: 'api', etiqueta: 'API', icon: Plug },
  { id: 'opciones', etiqueta: 'Operación y POS', icon: SlidersHorizontal },
  { id: 'app', etiqueta: 'App e impuestos', icon: Smartphone },
  { id: 'fidelidad', etiqueta: 'Fidelización', icon: Star },
  { id: 'integraciones', etiqueta: 'Consultas e integraciones', icon: Landmark },
  { id: 'logos', etiqueta: 'Logos e imágenes', icon: IconoImagen },
];

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-ink-50 disabled:opacity-70';

const BASE_PESTANA =
  'inline-flex shrink-0 items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors';
const PESTANA_INACTIVA =
  'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700';
const PESTANA_ACTIVA = 'bg-ink-900 text-white';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

// ---------------------------------------------------------------------------
// Controles del formulario
// ---------------------------------------------------------------------------

interface CampoTextoProps {
  etiqueta: string;
  valor: string;
  onCambio: (valor: string) => void;
  bloqueado: boolean;
  tipo?: 'text' | 'password' | 'date' | 'url' | 'email';
  ayuda?: string;
  maxLength?: number;
  marcador?: string;
  mono?: boolean;
  /** Control opcional dentro del campo (el botón de búsqueda del RUC). */
  accion?: ReactNode;
}

/** Campo de texto con la etiqueta del panel anterior. */
function CampoTexto({
  etiqueta,
  valor,
  onCambio,
  bloqueado,
  tipo = 'text',
  ayuda,
  maxLength,
  marcador,
  mono = false,
  accion,
}: CampoTextoProps) {
  return (
    <Input
      label={etiqueta}
      type={tipo}
      value={valor}
      hint={ayuda}
      maxLength={maxLength}
      placeholder={marcador}
      disabled={bloqueado}
      className={mono ? 'font-mono text-xs' : undefined}
      rightSlot={accion}
      onChange={(evento) => onCambio(evento.target.value)}
    />
  );
}

interface CampoNumeroProps {
  etiqueta: string;
  valor: number;
  onCambio: (valor: number) => void;
  bloqueado: boolean;
  ayuda?: string;
  min?: number;
  max?: number;
  paso?: number;
  sufijo?: string;
}

/** Campo numérico (las columnas `int`/`decimal`). */
function CampoNumero({
  etiqueta,
  valor,
  onCambio,
  bloqueado,
  ayuda,
  min,
  max,
  paso,
  sufijo,
}: CampoNumeroProps) {
  return (
    <Input
      label={etiqueta}
      type="number"
      value={String(valor)}
      hint={ayuda}
      min={min}
      max={max}
      step={paso}
      disabled={bloqueado}
      rightSlot={sufijo ? <span className="px-3 text-xs text-ink-400">{sufijo}</span> : undefined}
      onChange={(evento) => {
        const bruto = evento.target.value;
        onCambio(bruto === '' ? 0 : Number(bruto));
      }}
    />
  );
}

interface CampoSelectProps<T extends string> {
  etiqueta: string;
  valor: T;
  opciones: readonly Opcion<T>[];
  onCambio: (valor: T) => void;
  bloqueado: boolean;
  ayuda?: string;
}

/** Select con etiqueta (los `select` del panel anterior). */
function CampoSelect<T extends string>({
  etiqueta,
  valor,
  opciones,
  onCambio,
  bloqueado,
  ayuda,
}: CampoSelectProps<T>) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-700">
        {etiqueta}
      </label>
      <Select
        id={id}
        className={CONTROL}
        value={valor}
        disabled={bloqueado}
        onChange={(evento) => onCambio(evento.target.value as T)}
      >
        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.etiqueta}
          </option>
        ))}
      </Select>
      {ayuda ? <p className="text-xs text-ink-500">{ayuda}</p> : null}
    </div>
  );
}

/** Dato de sólo lectura. */
function Dato({ etiqueta, valor, mono = false }: { etiqueta: string; valor: string; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-control border border-ink-100 bg-ink-50/50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{etiqueta}</p>
      <p
        className={cn('truncate text-sm text-ink-800', mono && 'font-mono text-xs')}
        title={valor || undefined}
      >
        {valor || '—'}
      </p>
    </div>
  );
}

/** Rejilla de campos. */
function Rejilla({ children, columnas = 3 }: { children: ReactNode; columnas?: 2 | 3 | 4 }) {
  const clase =
    columnas === 2
      ? 'sm:grid-cols-2'
      : columnas === 4
        ? 'sm:grid-cols-2 xl:grid-cols-4'
        : 'sm:grid-cols-2 xl:grid-cols-3';

  return <div className={cn('grid gap-4', clase)}>{children}</div>;
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function EmpresaPage() {
  useDocumentTitle('Configuración · Empresa');

  const consulta = useEmpresa();
  const sinPermiso = consulta.error instanceof ApiError && consulta.error.status === 403;

  const cabecera = (
    <header>
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
      <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
        <Building2 className="size-6 text-brand-500" aria-hidden="true" />
        Empresa
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-500">
        Datos fiscales, comprobantes, opciones de operación, app y logos del sistema.
      </p>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldCheck className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  if (consulta.isLoading) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !consulta.data) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="error" title="No se pudo cargar la configuración">
          <div className="flex flex-col gap-3">
            <span>{mensajeDe(consulta.error, 'La API no respondió.')}</span>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => void consulta.refetch()}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  return <FormularioEmpresa inicial={consulta.data} cabecera={cabecera} />;
}

interface FormularioProps {
  inicial: EmpresaRespuesta;
  cabecera: ReactNode;
}

/** Formulario completo (modo lectura hasta pulsar *Editar*). */
function FormularioEmpresa({ inicial, cabecera }: FormularioProps) {
  const usuario = useAuthStore(selectUser);
  const puedeCambiarPlan = usuario?.id === 1;

  const [empresa, setEmpresa] = useState<EmpresaConfig>(inicial.empresa);
  const [extras, setExtras] = useState<ConfigCompanyPayload>(extrasDe(inicial.config_company));
  const [plan, setPlan] = useState(inicial.plan);
  const [pestana, setPestana] = useState<Pestana>('plan');
  const [editando, setEditando] = useState(false);
  const [confirmandoGuardado, setConfirmandoGuardado] = useState(false);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);

  const [dialogoPlan, setDialogoPlan] = useState(false);
  const [errorPlan, setErrorPlan] = useState<string | null>(null);
  const [resultadoRuc, setResultadoRuc] = useState<{
    tono: 'success' | 'error';
    texto: string;
  } | null>(null);
  const [imagenPendiente, setImagenPendiente] = useState<{
    campo: CampoImagenEmpresa;
    archivo: File;
    etiqueta: string;
  } | null>(null);
  const [errorImagen, setErrorImagen] = useState<string | null>(null);

  const guardar = useGuardarEmpresa();
  const cambiarPlan = useCambiarPlan();
  const subirImagen = useSubirImagenEmpresa();
  const consultarRuc = useConsultarRuc();

  // Ubigeo (departamento / provincia / distrito) desde el catálogo local.
  const [departamentos, setDepartamentos] = useState<OpcionUbigeo[]>([]);
  const [provincias, setProvincias] = useState<OpcionUbigeo[]>([]);
  const [distritos, setDistritos] = useState<OpcionUbigeo[]>([]);
  const [errorUbigeo, setErrorUbigeo] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    cargarUbigeo()
      .then((datos) => {
        if (vigente) setDepartamentos(datos.departamentos);
      })
      .catch((error: unknown) => {
        if (vigente) setErrorUbigeo(mensajeDe(error, 'No se pudo cargar el catálogo de ubigeo.'));
      });

    return () => {
      vigente = false;
    };
  }, []);

  useEffect(() => {
    let vigente = true;
    provinciasDe(empresa.direccion_departamento_id)
      .then((lista) => {
        if (vigente) setProvincias(lista);
      })
      .catch(() => {
        if (vigente) setProvincias([]);
      });

    return () => {
      vigente = false;
    };
  }, [empresa.direccion_departamento_id]);

  useEffect(() => {
    let vigente = true;
    distritosDe(empresa.direccion_provincia_id)
      .then((lista) => {
        if (vigente) setDistritos(lista);
      })
      .catch(() => {
        if (vigente) setDistritos([]);
      });

    return () => {
      vigente = false;
    };
  }, [empresa.direccion_provincia_id]);

  const bloqueado = !editando;
  /** Sólo se puede consultar el RUC editando y con 11 dígitos. */
  const puedeConsultarRuc = !bloqueado && empresa.ruc.replace(/\D/g, '').length === 11;

  function cambiar<K extends keyof EmpresaFormulario>(campo: K, valor: EmpresaFormulario[K]) {
    setEmpresa((previo) => ({ ...previo, [campo]: valor }));
  }

  function cambiarExtra<K extends keyof ConfigCompanyPayload>(
    campo: K,
    valor: ConfigCompanyPayload[K],
  ) {
    setExtras((previo) => ({ ...previo, [campo]: valor }));
  }

  const descartar = () => {
    setEmpresa(inicial.empresa);
    setExtras(extrasDe(inicial.config_company));
    setEditando(false);
    setAviso(null);
    setResultadoRuc(null);
  };

  /**
   * Botón «Buscar en SUNAT» del RUC (`consultar_ruc()` del panel anterior).
   *
   * El original volcaba la razón social en el nombre comercial y en la razón
   * social, y la dirección completa en la dirección fiscal; aquí se hace igual,
   * pero sólo se pisa un campo si SUNAT devolvió un valor (antes un campo vacío
   * borraba lo que ya estaba escrito).
   */
  const alConsultarRuc = () => {
    setResultadoRuc(null);

    consultarRuc.mutate(empresa.ruc.replace(/\D/g, ''), {
      onSuccess: ({ documento }) => {
        setEmpresa((previo) => ({
          ...previo,
          razon_social: documento.nombre || previo.razon_social,
          nom_comercial: documento.nombre || previo.nom_comercial,
          direccion: documento.direccion || previo.direccion,
        }));

        const detalles = [
          documento.estado ? `estado ${documento.estado}` : null,
          documento.condicion ? `condición ${documento.condicion}` : null,
        ].filter(Boolean);

        setResultadoRuc({
          tono: 'success',
          texto:
            `Datos del RUC ${documento.numero} obtenidos de SUNAT` +
            (detalles.length ? ` (${detalles.join(' · ')})` : '') +
            '. Se completaron la razón social, el nombre comercial y la dirección fiscal. ' +
            'Revisa los campos y guarda los cambios.',
        });
      },
      onError: (error) => {
        setResultadoRuc({
          tono: 'error',
          texto: mensajeDe(error, 'No se pudo consultar el RUC en SUNAT.'),
        });
      },
    });
  };

  const alGuardar = () => {
    setConfirmandoGuardado(false);
    setAviso(null);

    guardar.mutate(construirPayload(empresa, extras), {
      onSuccess: (datos) => {
        setEmpresa(datos.empresa);
        setExtras(extrasDe(datos.config_company));
        setPlan(datos.plan);
        setEditando(false);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setAviso({
          tono: 'error',
          texto: mensajeDe(error, 'No se pudieron guardar los cambios.'),
        });
      },
    });
  };

  const alCambiarPlan = (planId: number, tipo: 'mensual' | 'anual') => {
    setErrorPlan(null);
    cambiarPlan.mutate(
      { plan_id: planId, tipo },
      {
        onSuccess: (datos) => {
          setDialogoPlan(false);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => setErrorPlan(mensajeDe(error, 'No se pudo cambiar el plan.')),
      },
    );
  };

  const alSubirImagen = () => {
    if (!imagenPendiente) return;

    const { campo, archivo } = imagenPendiente;
    setErrorImagen(null);

    subirImagen.mutate(
      { campo, archivo },
      {
        onSuccess: (imagen) => {
          setEmpresa((previo) => ({
            ...previo,
            imagenes: { ...previo.imagenes, [imagen.campo]: imagen },
          }));
          setImagenPendiente(null);
        },
        onError: (error) => {
          setErrorImagen(mensajeDe(error, 'No se pudo subir la imagen.'));
          setImagenPendiente(null);
        },
      },
    );
  };

  const cambiarDepartamento = (id: string) => {
    setEmpresa((previo) => ({
      ...previo,
      direccion_departamento_id: id,
      direccion_provincia_id: '',
      direccion_distrito_id: '',
      direccion_departamento: '',
      direccion_provincia: '',
      direccion_distrito: '',
      codigo_ubigeo: '',
    }));
  };

  const cambiarProvincia = (id: string) => {
    setEmpresa((previo) => ({
      ...previo,
      direccion_provincia_id: id,
      direccion_distrito_id: '',
      direccion_provincia: '',
      direccion_distrito: '',
      codigo_ubigeo: '',
    }));
  };

  const cambiarDistrito = (id: string) => {
    const distrito = distritos.find((item) => item.id === id);
    const provincia = provincias.find((item) => item.id === empresa.direccion_provincia_id);
    const departamento = departamentos.find(
      (item) => item.id === empresa.direccion_departamento_id,
    );

    setEmpresa((previo) => ({
      ...previo,
      direccion_distrito_id: id,
      direccion_distrito: distrito?.name ?? '',
      direccion_provincia: provincia?.name ?? previo.direccion_provincia,
      direccion_departamento: departamento?.name ?? previo.direccion_departamento,
      codigo_ubigeo: id,
    }));
  };

  const opcionesUbigeo = (lista: OpcionUbigeo[]): readonly Opcion<string>[] =>
    lista.map((item) => ({ valor: item.id, etiqueta: item.name }));

  /** Contenido de la pestaña activa. */
  const contenido = () => {
    switch (pestana) {
      case 'plan':
        return (
          <EmpresaPlanCard
            plan={plan}
            puedeCambiar={puedeCambiarPlan}
            cambiando={cambiarPlan.isPending}
            error={errorPlan}
            onCambiar={() => {
              setErrorPlan(null);
              setDialogoPlan(true);
            }}
          />
        );

      case 'datos':
        return (
          <div className="flex flex-col gap-4">
            <SectionCard
              title="Identificación"
              description="Razón social y RUC con los que se emiten los comprobantes."
            >
              <div className="flex flex-col gap-4">
                <Rejilla>
                  <CampoTexto
                    etiqueta="RUC"
                    valor={empresa.ruc}
                    bloqueado={bloqueado}
                    maxLength={11}
                    ayuda="11 dígitos · la lupa trae los datos de SUNAT"
                    marcador="20123456789"
                    accion={
                      <button
                        type="button"
                        onClick={alConsultarRuc}
                        disabled={!puedeConsultarRuc}
                        title="Buscar en SUNAT"
                        aria-label="Buscar en SUNAT"
                        className="mr-1 flex size-7 items-center justify-center rounded-md text-ink-500 transition-colors hover:bg-brand-50 hover:text-brand-600 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {consultarRuc.isPending ? (
                          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                        ) : (
                          <Search className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    }
                    onCambio={(valor) => cambiar('ruc', valor)}
                  />
                  <CampoTexto
                    etiqueta="Nombre comercial"
                    valor={empresa.nom_comercial}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('nom_comercial', valor)}
                  />
                  <CampoTexto
                    etiqueta="Razón social"
                    valor={empresa.razon_social}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('razon_social', valor)}
                  />
                </Rejilla>

                {resultadoRuc ? (
                  <Alert variant={resultadoRuc.tono === 'success' ? 'success' : 'error'}>
                    {resultadoRuc.texto}
                  </Alert>
                ) : null}
              </div>
            </SectionCard>

            <SectionCard
              title="Dirección fiscal"
              description="El ubigeo se completa al elegir el distrito (como en el panel anterior)."
            >
              <div className="flex flex-col gap-4">
                {errorUbigeo ? <Alert variant="warning">{errorUbigeo}</Alert> : null}

                <CampoTexto
                  etiqueta="Dirección fiscal"
                  valor={empresa.direccion}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('direccion', valor)}
                />

                <Rejilla>
                  <CampoSelect
                    etiqueta="Departamento"
                    valor={empresa.direccion_departamento_id}
                    opciones={[{ valor: '', etiqueta: 'Seleccionar' }, ...opcionesUbigeo(departamentos)]}
                    bloqueado={bloqueado}
                    onCambio={cambiarDepartamento}
                  />
                  <CampoSelect
                    etiqueta="Provincia"
                    valor={empresa.direccion_provincia_id}
                    opciones={[{ valor: '', etiqueta: 'Seleccionar' }, ...opcionesUbigeo(provincias)]}
                    bloqueado={bloqueado}
                    onCambio={cambiarProvincia}
                  />
                  <CampoSelect
                    etiqueta="Distrito"
                    valor={empresa.direccion_distrito_id}
                    opciones={[{ valor: '', etiqueta: 'Seleccionar' }, ...opcionesUbigeo(distritos)]}
                    bloqueado={bloqueado}
                    onCambio={cambiarDistrito}
                  />
                </Rejilla>

                <Rejilla>
                  <CampoTexto
                    etiqueta="Código de ubigeo"
                    valor={empresa.codigo_ubigeo}
                    bloqueado
                    ayuda="Se completa con el distrito elegido"
                    onCambio={() => undefined}
                  />
                  <CampoTexto
                    etiqueta="Local"
                    valor={empresa.local}
                    bloqueado={bloqueado}
                    maxLength={4}
                    ayuda="Hasta 4 dígitos"
                    onCambio={(valor) => cambiar('local', valor)}
                  />
                  <CampoTexto
                    etiqueta="Código de país"
                    valor={empresa.direccion_codigopais}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('direccion_codigopais', valor)}
                  />
                </Rejilla>
              </div>
            </SectionCard>

            <SectionCard title="Contacto" description="Datos que se imprimen y se usan en las consultas.">
              <Rejilla>
                <CampoTexto
                  etiqueta="Teléfono"
                  valor={empresa.telefono}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('telefono', valor)}
                />
                <CampoTexto
                  etiqueta="Email"
                  valor={empresa.email}
                  bloqueado={bloqueado}
                  tipo="email"
                  onCambio={(valor) => cambiar('email', valor)}
                />
                <CampoTexto
                  etiqueta="Web emisor"
                  valor={empresa.web_emisor}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('web_emisor', valor)}
                />
                <CampoTexto
                  etiqueta="Web buscar (CPE)"
                  valor={empresa.web_site_cpe}
                  bloqueado={bloqueado}
                  ayuda="Se copia a la app como URL de consultas"
                  onCambio={(valor) => cambiar('web_site_cpe', valor)}
                />
                <CampoTexto
                  etiqueta="Web buscar todos"
                  valor={empresa.web_site_cpe_todos}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('web_site_cpe_todos', valor)}
                />
                <CampoTexto
                  etiqueta="Dirección de impresora"
                  valor={empresa.direccion_impresora}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('direccion_impresora', valor)}
                />
              </Rejilla>
            </SectionCard>

            <SectionCard
              title="Datos del comprobante"
              description="Aparecen en la cabecera de los comprobantes electrónicos."
            >
              <Rejilla>
                <CampoTexto
                  etiqueta="Nombre de sucursal"
                  valor={empresa.sucursal}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('sucursal', valor)}
                />
                <CampoTexto
                  etiqueta="Dirección del comprobante"
                  valor={empresa.dir_1_empresa}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('dir_1_empresa', valor)}
                />
                <CampoTexto
                  etiqueta="Ubicación del comprobante"
                  valor={empresa.dir_2_empresa}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('dir_2_empresa', valor)}
                />
                <CampoTexto
                  etiqueta="Almacén (API)"
                  valor={empresa.api_almacen_id}
                  bloqueado={bloqueado}
                  maxLength={4}
                  onCambio={(valor) => cambiar('api_almacen_id', valor)}
                />
              </Rejilla>
            </SectionCard>
          </div>
        );

      case 'api':
        return (
          <div className="flex flex-col gap-4">
            <SectionCard
              title="Facturación electrónica"
              description="Endpoints y credenciales del proveedor de comprobantes."
            >
              <Rejilla>
                <CampoTexto
                  etiqueta="Dirección API"
                  valor={empresa.api_dir_empresa}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('api_dir_empresa', valor)}
                />
                <CampoTexto
                  etiqueta="Token"
                  valor={empresa.token}
                  bloqueado={bloqueado}
                  mono
                  onCambio={(valor) => cambiar('token', valor)}
                />
                <CampoTexto
                  etiqueta="URL de consulta de ubigeo"
                  valor={empresa.dir_api_ubigeo}
                  bloqueado={bloqueado}
                  tipo="url"
                  marcador="https://ubigeo.escienza.pe/"
                  onCambio={(valor) => cambiar('dir_api_ubigeo', valor)}
                />
                <CampoTexto
                  etiqueta="Dirección API (administrador)"
                  valor={empresa.api_dir_empresa_administrador}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('api_dir_empresa_administrador', valor)}
                />
                <CampoTexto
                  etiqueta="Token (administrador)"
                  valor={empresa.token_administrador}
                  bloqueado={bloqueado}
                  mono
                  onCambio={(valor) => cambiar('token_administrador', valor)}
                />
                <CampoTexto
                  etiqueta="NCI sucursal"
                  valor={empresa.nci_sucursal}
                  bloqueado={bloqueado}
                  maxLength={15}
                  ayuda="Hasta 15 letras"
                  onCambio={(valor) => cambiar('nci_sucursal', valor.toUpperCase())}
                />
                <CampoTexto
                  etiqueta="Serie de guía de remisión"
                  valor={empresa.serie_guia_remision}
                  bloqueado={bloqueado}
                  maxLength={10}
                  onCambio={(valor) => cambiar('serie_guia_remision', valor)}
                />
                <CampoTexto
                  etiqueta="Serie de cotización"
                  valor={empresa.serie_cotizacion}
                  bloqueado={bloqueado}
                  maxLength={10}
                  onCambio={(valor) => cambiar('serie_cotizacion', valor)}
                />
                <CampoTexto
                  etiqueta="Vencimiento del certificado digital"
                  valor={empresa.fecha_vencimiento_certificado_digital}
                  bloqueado={bloqueado}
                  tipo="date"
                  onCambio={(valor) => cambiar('fecha_vencimiento_certificado_digital', valor)}
                />
              </Rejilla>
            </SectionCard>
          </div>
        );

      case 'opciones':
        return (
          <div className="flex flex-col gap-4">
            <SectionCard title="Opciones generales" description="Comportamiento por defecto del punto de venta.">
              <div className="flex flex-col gap-4">
                <Rejilla>
                  <CampoSelect
                    etiqueta="Sumar nota de venta"
                    valor={empresa.sumar_total_combustible_nota_venta}
                    opciones={SI_NO}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('sumar_total_combustible_nota_venta', valor)}
                  />
                  <CampoSelect
                    etiqueta="Habilitar precierre de turno"
                    valor={empresa.precierre_turno}
                    opciones={SI_NO}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('precierre_turno', valor)}
                  />
                  <CampoSelect
                    etiqueta="Habilitar nota de venta"
                    valor={empresa.generar_nota_venta}
                    opciones={SI_NO}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('generar_nota_venta', valor)}
                  />
                  <CampoNumero
                    etiqueta="Límite de transacciones"
                    valor={empresa.transactions_limit}
                    bloqueado={bloqueado}
                    min={0}
                    ayuda="Disponibles para facturar en el POS"
                    onCambio={(valor) => cambiar('transactions_limit', valor)}
                  />
                  <CampoSelect
                    etiqueta="Tipo de cierre"
                    valor={empresa.closure}
                    opciones={CIERRES}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiar('closure', valor)}
                  />
                  <CampoTexto
                    etiqueta="Cuenta bancaria para detracciones"
                    valor={empresa.cuenta_bancaria_detraccion}
                    bloqueado={bloqueado}
                    maxLength={50}
                    onCambio={(valor) => cambiar('cuenta_bancaria_detraccion', valor)}
                  />
                </Rejilla>

                <CampoTexto
                  etiqueta="Leyenda para los imprimibles (Ley 27037)"
                  valor={empresa.legend_27037}
                  bloqueado={bloqueado}
                  maxLength={192}
                  onCambio={(valor) => cambiar('legend_27037', valor)}
                />
              </div>
            </SectionCard>

            <SectionCard title="Sistema de ticketing (CPE)" description="Límites de la venta libre.">
              <Rejilla>
                <CampoNumero
                  etiqueta="Mínimo de transacciones"
                  valor={empresa.ticketing_min_transactions}
                  bloqueado={bloqueado}
                  min={1}
                  max={10}
                  onCambio={(valor) => cambiar('ticketing_min_transactions', valor)}
                />
                <CampoNumero
                  etiqueta="Máximo de transacciones"
                  valor={empresa.ticketing_max_transactions}
                  bloqueado={bloqueado}
                  min={1}
                  max={10}
                  onCambio={(valor) => cambiar('ticketing_max_transactions', valor)}
                />
                <CampoNumero
                  etiqueta="Monto mínimo faltante"
                  valor={empresa.monto_minimo_faltante}
                  bloqueado={bloqueado}
                  min={0}
                  max={3}
                  paso={0.01}
                  sufijo="S/"
                  onCambio={(valor) => cambiar('monto_minimo_faltante', valor)}
                />
                <CampoNumero
                  etiqueta="Umbral de RIC en rojo"
                  valor={empresa.ric_difference_red_threshold}
                  bloqueado={bloqueado}
                  min={0}
                  paso={1}
                  ayuda="Diferencia (galones) a partir de la cual la medición se marca en rojo"
                  onCambio={(valor) => cambiar('ric_difference_red_threshold', valor)}
                />
              </Rejilla>
            </SectionCard>

            <SectionCard title="Opciones para administración" description="Anulaciones y boletas masivas.">
              <Rejilla>
                <CampoSelect
                  etiqueta="Botón de último turno"
                  valor={empresa.crd_boton_ultimo_turno}
                  opciones={SI_NO}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('crd_boton_ultimo_turno', valor)}
                />
                <CampoNumero
                  etiqueta="Días máximos de boletas"
                  valor={empresa.dias_boleta_masiva}
                  bloqueado={bloqueado}
                  min={0}
                  onCambio={(valor) => cambiar('dias_boleta_masiva', valor)}
                />
                <CampoNumero
                  etiqueta="Precio máximo de administración"
                  valor={empresa.precio_producto_boleta_masiva}
                  bloqueado={bloqueado}
                  min={0}
                  paso={0.01}
                  sufijo="S/"
                  onCambio={(valor) => cambiar('precio_producto_boleta_masiva', valor)}
                />
                <CampoNumero
                  etiqueta="Días máximos para anular"
                  valor={empresa.dias_maximos_anular}
                  bloqueado={bloqueado}
                  min={0}
                  max={10}
                  ayuda="Máximo 10 días"
                  onCambio={(valor) => cambiar('dias_maximos_anular', valor)}
                />
              </Rejilla>
            </SectionCard>

            <SectionCard title="Opciones para griferos" description="Permisos del personal en el POS.">
              <Rejilla>
                <CampoSelect
                  etiqueta="Ingreso manual de datos del cliente"
                  valor={empresa.crd_habilitar_campos_ingreso_manual_consulta_cliente}
                  opciones={SI_NO}
                  bloqueado={bloqueado}
                  onCambio={(valor) =>
                    cambiar('crd_habilitar_campos_ingreso_manual_consulta_cliente', valor)
                  }
                />
                <CampoSelect
                  etiqueta="Documentos de transacciones pasadas"
                  valor={aTextoBandera(empresa.configPastTransactions)}
                  opciones={BANDERA}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('configPastTransactions', aBandera(valor))}
                />
                <CampoSelect
                  etiqueta="Editar contómetros finales"
                  valor={aTextoBandera(empresa.editMeters)}
                  opciones={BANDERA}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('editMeters', aBandera(valor))}
                />
                <CampoSelect
                  etiqueta="Mostrar histórico de ventas"
                  valor={aTextoBandera(empresa.show_historic)}
                  opciones={BANDERA}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('show_historic', aBandera(valor))}
                />
                <CampoSelect
                  etiqueta="Editar el método de pago"
                  valor={aTextoBandera(empresa.edit_payment)}
                  opciones={BANDERA}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('edit_payment', aBandera(valor))}
                />
              </Rejilla>
            </SectionCard>
          </div>
        );

      case 'app':
        return (
          <div className="flex flex-col gap-4">
            <SectionCard
              title="App e impuestos"
              description="Estos valores viven en config_company (los que consume la app) y no en la configuración del sistema."
            >
              <div className="flex flex-col gap-4">
                <Rejilla>
                  <CampoSelect
                    etiqueta="IGV exonerada"
                    valor={aTextoBandera(extras.igv_exonerada)}
                    opciones={BANDERA}
                    bloqueado={bloqueado}
                    ayuda="Controla también el modal de IGV en la app"
                    onCambio={(valor) => cambiarExtra('igv_exonerada', aBandera(valor))}
                  />
                  <CampoNumero
                    etiqueta="Valor para cálculos de IGV"
                    valor={extras.igc_percentage}
                    bloqueado={bloqueado}
                    min={0}
                    max={100}
                    paso={0.01}
                    sufijo="%"
                    onCambio={(valor) => cambiarExtra('igc_percentage', valor)}
                  />
                  <CampoSelect
                    etiqueta="Mostrar créditos"
                    valor={extras.showCredit}
                    opciones={CREDITOS}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('showCredit', valor)}
                  />
                  <CampoSelect
                    etiqueta="Mostrar pago adelantado"
                    valor={extras.mostrar_pago_adelantado}
                    opciones={VISIBILIDAD}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('mostrar_pago_adelantado', valor)}
                  />
                  <CampoSelect
                    etiqueta="Mostrar CardPlus"
                    valor={extras.mostrar_cardplus}
                    opciones={VISIBILIDAD}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('mostrar_cardplus', valor)}
                  />
                  <CampoSelect
                    etiqueta="Visualización del descuento"
                    valor={extras.modo_visualizacion_descuento}
                    opciones={MODOS_DESCUENTO}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('modo_visualizacion_descuento', valor)}
                  />
                  <CampoSelect
                    etiqueta="Fidelidad en Market"
                    valor={aTextoBandera(extras.loyaltyInMarket)}
                    opciones={BANDERA}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('loyaltyInMarket', aBandera(valor))}
                  />
                  <CampoSelect
                    etiqueta="Tipo de documento de auto-ticketing"
                    valor={extras.tipo_documento_autoticketing}
                    opciones={TIPOS_DOC_AUTOTICKETING}
                    bloqueado={bloqueado}
                    ayuda="Las ventas que alcanzan el monto máximo anónimo de la empresa se emiten siempre como Nota de Venta (no como CPE); el resto, con el tipo elegido aquí."
                    onCambio={(valor) => cambiarExtra('tipo_documento_autoticketing', valor)}
                  />
                  <CampoSelect
                    etiqueta="NFC en el POS"
                    valor={aTextoBandera(extras.nfc_habilitado_pos)}
                    opciones={NFC}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('nfc_habilitado_pos', aBandera(valor))}
                  />
                  <CampoNumero
                    etiqueta="Máximo de transacciones libres por turno"
                    valor={extras.max_free_transactions_per_shift}
                    bloqueado={bloqueado}
                    min={1}
                    max={100}
                    onCambio={(valor) => cambiarExtra('max_free_transactions_per_shift', valor)}
                  />
                  <CampoSelect
                    etiqueta="Modo de trabajo"
                    valor={extras.modo_trabajo}
                    opciones={MODOS_TRABAJO}
                    bloqueado={bloqueado}
                    onCambio={(valor) => cambiarExtra('modo_trabajo', valor)}
                  />
                  <CampoSelect
                    etiqueta="Impresión de CPE múltiple"
                    valor={aTextoBandera(extras.agrupar_productos_cpe_multiple)}
                    opciones={IMPRESION_CPE}
                    bloqueado={bloqueado}
                    onCambio={(valor) =>
                      cambiarExtra('agrupar_productos_cpe_multiple', aBandera(valor))
                    }
                  />
                </Rejilla>
              </div>
            </SectionCard>

            <SectionCard
              title="Datos que usa la app"
              description="Valores de config_company que consume la app: moneda, idioma, IGV y montos."
            >
              <Rejilla>
                <CampoSelect
                  etiqueta="Moneda"
                  valor={extras.currency_type}
                  opciones={(inicial.monedas ?? []).map((moneda) => ({
                    valor: moneda.id,
                    etiqueta: `${moneda.descripcion || moneda.id} (${moneda.id})`,
                  }))}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiarExtra('currency_type', valor)}
                />
                <CampoSelect
                  etiqueta="Idioma"
                  valor={extras.language}
                  opciones={IDIOMAS}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiarExtra('language', valor)}
                />
                <CampoTexto
                  etiqueta="Estado tributario del IGV"
                  valor={extras.igv_tax_status}
                  bloqueado={bloqueado}
                  maxLength={10}
                  ayuda="Código SUNAT del tipo de afectación (p. ej. 10)"
                  onCambio={(valor) => cambiarExtra('igv_tax_status', valor)}
                />
                <CampoNumero
                  etiqueta="Monto máximo anónimo"
                  valor={extras.monto_maximo_anonimo}
                  bloqueado={bloqueado}
                  min={0}
                  paso={0.01}
                  sufijo="S/"
                  onCambio={(valor) => cambiarExtra('monto_maximo_anonimo', valor)}
                />
                <CampoTexto
                  etiqueta="URL de descarga de fidelidad"
                  valor={extras.loyalty_url_download}
                  bloqueado={bloqueado}
                  tipo="url"
                  maxLength={500}
                  marcador="https://…"
                  onCambio={(valor) => cambiarExtra('loyalty_url_download', valor)}
                />
              </Rejilla>
            </SectionCard>

            <SectionCard
              title="Datos de la app de sólo lectura"
              description="Se calculan solos, los administra la app o se editan en otra pestaña."
            >
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <Dato
                  etiqueta="Comprobante por defecto"
                  valor={inicial.config_company?.comprobante ?? ''}
                />
                <Dato
                  etiqueta="Modal de IGV"
                  valor={inicial.config_company?.showIgvModal === 1 ? 'Sí' : 'No'}
                />
                <Dato
                  etiqueta="Tipo de fidelidad (app)"
                  valor={inicial.config_company?.loyalty_type ?? ''}
                />
                <Dato
                  etiqueta="Última sincronización"
                  valor={inicial.config_company?.actualizado_en ?? ''}
                />
                <Dato
                  etiqueta="Security token"
                  valor={inicial.config_company?.security_token ?? ''}
                  mono
                />
              </div>

              <p className="mt-3 text-xs text-ink-400">
                El comprobante por defecto se cambia en <strong>Operación y POS</strong>; el modal
                de IGV sigue a <strong>IGV exonerada</strong> y el tipo de fidelidad a la pestaña{' '}
                <strong>Fidelización</strong>.
              </p>
            </SectionCard>
          </div>
        );

      case 'fidelidad':
        return (
          <SectionCard
            title="Programa de fidelización"
            description="El sistema elegido define qué campos se usan y qué se copia a la app."
          >
            <div className="flex flex-col gap-4">
              <Rejilla>
                <CampoSelect
                  etiqueta="Sistema de fidelidad"
                  valor={empresa.fidelity}
                  opciones={FIDELIDADES}
                  bloqueado={bloqueado}
                  ayuda="Al guardar, el indicador interno `bonus` se recalcula solo"
                  onCambio={(valor) => cambiar('fidelity', valor)}
                />
                <CampoTexto
                  etiqueta="Usuario de CardPlus"
                  valor={empresa.cardplus_auth_user}
                  bloqueado={bloqueado || empresa.fidelity !== 'cardplus'}
                  onCambio={(valor) => cambiar('cardplus_auth_user', valor)}
                />
                <CampoTexto
                  etiqueta="Password de CardPlus"
                  valor={empresa.cardplus_auth_pwd}
                  bloqueado={bloqueado || empresa.fidelity !== 'cardplus'}
                  tipo="password"
                  onCambio={(valor) => cambiar('cardplus_auth_pwd', valor)}
                />
                <CampoTexto
                  etiqueta="Loyalty Code"
                  valor={extras.loyalty_code}
                  bloqueado={bloqueado || empresa.fidelity !== 'cardplus'}
                  ayuda="Código de la estación en CardPlus"
                  onCambio={(valor) => cambiarExtra('loyalty_code', valor)}
                />
              </Rejilla>

              {empresa.fidelity === 'bonus' ? (
                <Rejilla columnas={2}>
                  <CampoTexto
                    etiqueta="Commerce Code"
                    valor={empresa.codigo_bonus}
                    bloqueado={bloqueado}
                    maxLength={6}
                    ayuda="6 caracteres"
                    onCambio={(valor) => cambiar('codigo_bonus', valor)}
                  />
                  <CampoTexto
                    etiqueta="Código de suscripción"
                    valor={empresa.bonus_subscription_code}
                    bloqueado={bloqueado}
                    maxLength={32}
                    onCambio={(valor) => cambiar('bonus_subscription_code', valor)}
                  />
                </Rejilla>
              ) : (
                <Alert variant="info">
                  <span className={ICONO_EN_ALERTA}>
                    <CreditCard className="size-4" aria-hidden="true" />
                    El <strong>Commerce Code</strong> y el <strong>código de suscripción</strong> sólo
                    se usan con el sistema <strong>Bonus</strong>.
                  </span>
                </Alert>
              )}
            </div>
          </SectionCard>
        );

      case 'integraciones':
        return (
          <div className="flex flex-col gap-4">
            <SectionCard
              title="Consulta de RUC / DNI"
              description="Servidor alterno y credenciales del padrón de consultas."
            >
              <Rejilla>
                <CampoSelect
                  etiqueta="Servidor alterno"
                  valor={empresa.crd_servidor_alterno}
                  opciones={SI_NO}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('crd_servidor_alterno', valor)}
                />
                <CampoTexto
                  etiqueta="URL principal"
                  valor={empresa.crd_url_principal}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('crd_url_principal', valor)}
                />
                <CampoTexto
                  etiqueta="Token principal"
                  valor={empresa.crd_token_principal}
                  bloqueado={bloqueado}
                  mono
                  onCambio={(valor) => cambiar('crd_token_principal', valor)}
                />
                <CampoTexto
                  etiqueta="URL secundaria"
                  valor={empresa.crd_url_secundario}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('crd_url_secundario', valor)}
                />
                <CampoTexto
                  etiqueta="Token secundario"
                  valor={empresa.crd_token_secundario}
                  bloqueado={bloqueado}
                  mono
                  onCambio={(valor) => cambiar('crd_token_secundario', valor)}
                />
              </Rejilla>
            </SectionCard>

            <SectionCard
              title="Configuración Hermes"
              description="Conectividad con el sistema de facturación electrónica Hermes."
            >
              <Rejilla>
                <CampoTexto
                  etiqueta="URL de Hermes"
                  valor={empresa.hermes_url}
                  bloqueado={bloqueado}
                  tipo="url"
                  onCambio={(valor) => cambiar('hermes_url', valor)}
                />
                <CampoTexto
                  etiqueta="Usuario"
                  valor={empresa.hermes_user}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('hermes_user', valor)}
                />
                <CampoTexto
                  etiqueta="Contraseña"
                  valor={empresa.hermes_password}
                  bloqueado={bloqueado}
                  tipo="password"
                  onCambio={(valor) => cambiar('hermes_password', valor)}
                />
                <CampoTexto
                  etiqueta="Equipo"
                  valor={empresa.hermes_equipment}
                  bloqueado={bloqueado}
                  onCambio={(valor) => cambiar('hermes_equipment', valor)}
                />
                <CampoTexto
                  etiqueta="API Key"
                  valor={empresa.hermes_apikey}
                  bloqueado={bloqueado}
                  mono
                  onCambio={(valor) => cambiar('hermes_apikey', valor)}
                />
              </Rejilla>
            </SectionCard>
          </div>
        );

      case 'logos':
        return (
          <EmpresaLogos
            imagenes={empresa.imagenes}
            subiendoCampo={subirImagen.isPending ? subirImagen.variables?.campo ?? null : null}
            error={errorImagen}
            onElegir={(campo, archivo) =>
              setImagenPendiente({
                campo,
                archivo,
                etiqueta: empresa.imagenes[campo].etiqueta,
              })
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <nav className="flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0" aria-label="Secciones de la empresa">
          {PESTANAS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-current={pestana === item.id ? 'page' : undefined}
              className={cn(BASE_PESTANA, pestana === item.id ? PESTANA_ACTIVA : PESTANA_INACTIVA)}
              onClick={() => setPestana(item.id)}
            >
              <item.icon className="size-3.5" aria-hidden="true" />
              {item.etiqueta}
            </button>
          ))}
        </nav>

        <div className="flex flex-wrap items-center gap-2">
          {editando ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<X className="size-3.5" aria-hidden="true" />}
                disabled={guardar.isPending}
                onClick={descartar}
              >
                Descartar
              </Button>
              <Button
                size="sm"
                leftIcon={<Save className="size-3.5" aria-hidden="true" />}
                loading={guardar.isPending}
                onClick={() => setConfirmandoGuardado(true)}
              >
                Guardar cambios
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              leftIcon={<Pencil className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                setAviso(null);
                setEditando(true);
              }}
            >
              Editar
            </Button>
          )}
        </div>
      </div>

      {!editando ? (
        <Alert variant="info">
          <span className={ICONO_EN_ALERTA}>
            <ShieldCheck className="size-4" aria-hidden="true" />
            Estás viendo la configuración en modo lectura. Pulsa <strong>Editar</strong> para
            habilitar los campos.
          </span>
        </Alert>
      ) : null}

      {contenido()}

      {pestana === 'plan' ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <CalendarClock className="size-4 text-ink-400" aria-hidden="true" />
              Planes disponibles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {inicial.planes.map((disponible) => (
                <div
                  key={disponible.id}
                  className="flex flex-col gap-1 rounded-control border border-ink-100 px-4 py-3"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: disponible.color }}
                    />
                    <span className="text-sm font-semibold text-ink-900">{disponible.nombre}</span>
                    {plan?.id === disponible.id ? <Badge variant="brand">Actual</Badge> : null}
                  </span>
                  <span className="text-xs text-ink-500">
                    {textoModulos(disponible.total_modulos)} · {formatCurrency(disponible.precio_mensual)} /mes
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {dialogoPlan ? (
        <EmpresaPlanDialogo
          key={`plan-${plan?.id ?? 0}-${plan?.tipo ?? 'mensual'}`}
          open
          onClose={() => setDialogoPlan(false)}
          planes={inicial.planes}
          planActual={plan}
          guardando={cambiarPlan.isPending}
          error={errorPlan}
          onConfirmar={alCambiarPlan}
        />
      ) : null}

      <ConfirmDialog
        open={confirmandoGuardado}
        tone="primary"
        loading={guardar.isPending}
        title="¿Guardar la configuración de la empresa?"
        description="Se actualizarán los datos fiscales, los comprobantes y la configuración que usa la app."
        confirmLabel="Sí, guardar"
        onClose={() => setConfirmandoGuardado(false)}
        onConfirm={alGuardar}
      />

      <ConfirmDialog
        open={imagenPendiente !== null}
        title={`¿Actualizar ${imagenPendiente?.etiqueta ?? 'la imagen'}?`}
        description={`Se reemplazará la imagen actual por «${imagenPendiente?.archivo.name ?? ''}».`}
        confirmLabel="Sí, subir"
        onClose={() => setImagenPendiente(null)}
        onConfirm={alSubirImagen}
      />
    </div>
  );
}
